import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { publicReadRetry } from '@/lib/retry-policy'
import { authGatedReadRetry } from '@/hooks/queries'
import { trackRegulatoryWatchAdded } from '@/lib/analytics-events'

/**
 * Query hooks for the wave-1 `/regulatory` page.
 *
 * The consumed endpoints live in `apps/api/src/routes/regulatory.ts`:
 *  - GET /regulatory/velocity — P4 Regulator Velocity (Stage 1)
 *  - GET /regulatory/kpis     — Stage-1 KPI rail (3 cards)
 *
 * `API_BASE_URL` defaults to `/api`, so paths passed here must NOT include
 * the `/api` prefix (double-prefix would hit the SPA catch-all and cause a
 * silent auth loop — see `apps/web/CLAUDE.md`).
 */

export type RegulatoryTimeframe = '30d' | '90d' | '365d' | 'all'
export type RegulatoryJurisdiction = 'all' | 'EU' | 'US' | 'UK' | 'International'

export type RegulatoryFilters = {
  jurisdiction: RegulatoryJurisdiction
  framework: string // 'all' or slug — Stage 2+ populates the slug list.
  timeframe: RegulatoryTimeframe
}

export type VelocityAuthority = {
  authority: string
  recent: number
  prior: number
}

export type VelocityResponse = {
  window: RegulatoryTimeframe
  authorities: VelocityAuthority[]
}

export type KpiResponse = {
  /**
   * Echo of the resolved timeframe — drives dynamic tile labels in
   * `RegulatoryKpiRail` (e.g. `"Last 30D"` vs `"Last 90D"`). Server defaults
   * to `'90d'` when omitted. P4f (audit item 22).
   */
  window: RegulatoryTimeframe
  /** Echo of the resolved jurisdiction filter. */
  jurisdiction: RegulatoryJurisdiction
  activeRegulators: number
  /** Window-aware action count — replaces `actions90d`. */
  actionsWindow: number
  /** Window-aware fine volume — replaces `fineVolume90d`. */
  fineVolumeWindow: number
  frameworksTracked: number
  /**
   * Backwards-compat aliases — server still emits these so existing
   * callers (snapshot tests, older components) keep working until removed.
   * New code should consume `actionsWindow` / `fineVolumeWindow` + `window`.
   */
  actions90d: number
  fineVolume90d: number
}

export type FrameworkCoverageStatus =
  | 'full'
  | 'partial'
  | 'emerging'
  | 'pending-ingest'

export type FrameworkJurisdiction = 'EU' | 'US' | 'UK' | 'International'

export type FrameworkRow = {
  id: string
  slug: string
  name: string
  shortDescription: string
  jurisdiction: FrameworkJurisdiction
  referenceUrl: string
  coverageStatus: FrameworkCoverageStatus
  sortOrder: number
  lastEnforcementAt: string | null
  /**
   * KAN-274 F5: true only when the framework's detail page can back a real
   * "Full coverage" claim — detail surface enabled, not archived, and a
   * published brief exists. Derived server-side (see `regulatory.ts`). Lets
   * the coverage grid downgrade an over-claiming `coverageStatus='full'` card
   * to an honest in-progress badge.
   */
  detailAvailable: boolean
}

export type FrameworksResponse = {
  frameworks: FrameworkRow[]
}

export type RegulatoryBriefCoverageState = 'full' | 'partial'
export type RegulatoryBriefStatus = 'draft' | 'published' | 'archived'

export type RegulatoryApplicability = {
  jurisdiction?: string[]
  sector?: string[]
  size_band?: string[]
  activity_type?: string[]
  [key: string]: string[] | string | number | boolean | null | undefined
}

export type RegulatoryKeyObligation = {
  title: string
  description?: string
  citation_url?: string | null
}

export type RegulatoryPenaltyStructure = {
  max_fine_eur?: string | null
  formula_text?: string
  criminal_liability?: boolean
  [key: string]: string | number | boolean | null | undefined
}

export type RegulatoryRecentAmendment = {
  date: string
  headline: string
  source_url: string
}

/**
 * KAN-271 detail content — a reference to a key annex/schedule of the
 * regulation (e.g. `{ ref: 'Annex I', label: 'High-risk AI systems' }`).
 */
export type RegulatoryKeyAnnexRef = {
  ref: string
  label: string
}

export type RegulatoryFrameworkDetailBrief = {
  id: string
  status: RegulatoryBriefStatus
  coverageState: RegulatoryBriefCoverageState
  scopeSummary: string
  applicability: RegulatoryApplicability
  keyObligations: RegulatoryKeyObligation[]
  penaltyStructure: RegulatoryPenaltyStructure
  recentAmendments: RegulatoryRecentAmendment[]
  coverageRationale: string
  crosswalk: Record<string, unknown>
  analystPublicNotes: string
  needsReview: boolean
  citationLog: unknown[]
  // KAN-271 detail content (versioned with the brief).
  inForceDate: string | null
  supervisoryAuthority: string
  geographicScope: string
  keyAnnexesRefs: RegulatoryKeyAnnexRef[]
  lastDraftedAt: string
  reviewedAt: string | null
  updatedAt: string
}

export type RegulatoryFrameworkDetailDeadline = {
  id: string
  frameworkId: string
  kind: string
  dueDate: string
  label: string
  description: string | null
  sourceUrl: string
}

export type RegulatoryFrameworkDetailEnforcementAction = {
  id: string
  organisationName: string
  jurisdiction: string | null
  decisionDate: string | null
  severity: string | null
  sourceUrl: string | null
  summary: string | null
}

/**
 * Framework identity returned at every detail state. Extends the list-row
 * shape with the detail-only provenance columns surfaced by the projector
 * (`sourceCitation`, plus KAN-271 `regCitation` / `frameworkType`).
 */
export type RegulatoryFrameworkDetailFramework = Omit<
  FrameworkRow,
  'sortOrder' | 'lastEnforcementAt' | 'detailAvailable'
> & {
  sourceCitation: string | null
  regCitation: string | null
  frameworkType: string | null
  // Gate metadata is selected by the projector; the page does not read it
  // directly (the server resolves the discriminator), but it is part of the
  // identity payload.
  detailEnabled?: boolean
  archivedAt?: string | null
}

/**
 * `GET /regulatory/frameworks/:slug` is a discriminated union on `state`
 * (KAN-271 s4). The full brief/deadlines/enforcement payload only exists on
 * the `'ready'` arm; `'planned'` (detail_enabled=false) and `'missing_brief'`
 * (enabled but no brief drafted yet) carry framework identity only. Archived
 * and unknown slugs are HTTP 404 (handled via `ApiError`, not this union).
 */
export type RegulatoryFrameworkDetailReadyResponse = {
  state: 'ready'
  framework: RegulatoryFrameworkDetailFramework
  brief: RegulatoryFrameworkDetailBrief
  coverageState: RegulatoryBriefCoverageState
  status: RegulatoryBriefStatus
  deadlines: RegulatoryFrameworkDetailDeadline[]
  enforcement: {
    count90d: number
    latestDecisionAt: string | null
    recent: RegulatoryFrameworkDetailEnforcementAction[]
  }
}

export type RegulatoryFrameworkDetailPlannedResponse = {
  state: 'planned'
  framework: RegulatoryFrameworkDetailFramework
}

export type RegulatoryFrameworkDetailMissingBriefResponse = {
  state: 'missing_brief'
  framework: RegulatoryFrameworkDetailFramework
}

export type RegulatoryFrameworkDetailResponse =
  | RegulatoryFrameworkDetailReadyResponse
  | RegulatoryFrameworkDetailPlannedResponse
  | RegulatoryFrameworkDetailMissingBriefResponse

export function useRegulatoryVelocity(
  filters: Pick<RegulatoryFilters, 'jurisdiction' | 'timeframe'>,
) {
  const qs = new URLSearchParams({
    jurisdiction: filters.jurisdiction,
    timeframe: filters.timeframe,
  }).toString()
  return useQuery({
    queryKey: ['regulatory-velocity', filters.jurisdiction, filters.timeframe],
    retry: publicReadRetry,
    queryFn: () => api.get<VelocityResponse>(`/regulatory/velocity?${qs}`),
  })
}

/**
 * P4f (audit item 22): both `useRegulatoryKpis` and `useRegulatoryFrameworks`
 * accept an OPTIONAL `filters` argument. When omitted, the underlying
 * endpoint is hit with no querystring and the server applies its defaults
 * (`timeframe=90d`, `jurisdiction=all`) — preserves backwards compatibility
 * for any caller that doesn't yet thread filter state. When provided, the
 * filter values flow into the query key + querystring so toggling the page
 * filter rail correctly invalidates and refetches with the new window.
 */
export function useRegulatoryKpis(
  filters?: Pick<RegulatoryFilters, 'jurisdiction' | 'timeframe'>,
) {
  const qs = filters
    ? '?' +
      new URLSearchParams({
        jurisdiction: filters.jurisdiction,
        timeframe: filters.timeframe,
      }).toString()
    : ''
  return useQuery({
    queryKey: [
      'regulatory-kpis',
      filters?.jurisdiction ?? 'default',
      filters?.timeframe ?? 'default',
    ],
    retry: publicReadRetry,
    queryFn: () => api.get<KpiResponse>(`/regulatory/kpis${qs}`),
  })
}

export function useRegulatoryFrameworks(
  filters?: Pick<RegulatoryFilters, 'jurisdiction' | 'timeframe'>,
) {
  const qs = filters
    ? '?' +
      new URLSearchParams({
        jurisdiction: filters.jurisdiction,
        timeframe: filters.timeframe,
      }).toString()
    : ''
  return useQuery({
    queryKey: [
      'regulatory-frameworks',
      filters?.jurisdiction ?? 'default',
      filters?.timeframe ?? 'default',
    ],
    retry: publicReadRetry,
    queryFn: () =>
      api.get<FrameworksResponse>(`/regulatory/frameworks${qs}`),
  })
}

export function useRegulatoryFrameworkDetail(slug: string | undefined) {
  return useQuery({
    queryKey: ['regulatory-framework-detail', slug ?? 'missing'],
    enabled: Boolean(slug),
    // A 404 is a terminal answer for this slug (framework missing or its brief
    // is not yet drafted) — retrying just delays the empty-state. The shared
    // policy covers that case and every other permanent 4xx alongside it,
    // with the same 2-retry cap this hook already used.
    retry: publicReadRetry,
    queryFn: () =>
      api.get<RegulatoryFrameworkDetailResponse>(
        `/regulatory/frameworks/${encodeURIComponent(slug ?? '')}`,
      ),
  })
}

export type FineTrendQuarter = {
  quarter: string
  medianFine: number
  p95Fine: number
  count: number
}

export type FineTrendRow = {
  slug: string
  name: string
  quarters: FineTrendQuarter[]
}

export type FineTrendsResponse = {
  frameworks: FineTrendRow[]
}

export function useRegulatoryFineTrends(
  filters: Pick<RegulatoryFilters, 'jurisdiction' | 'timeframe'>,
) {
  const qs = new URLSearchParams({
    jurisdiction: filters.jurisdiction,
    timeframe: filters.timeframe,
  }).toString()
  return useQuery({
    queryKey: ['regulatory-fine-trends', filters.jurisdiction, filters.timeframe],
    retry: publicReadRetry,
    queryFn: () =>
      api.get<FineTrendsResponse>(`/regulatory/fine-trends?${qs}`),
  })
}

export type RegulatoryDeadlineKind =
  | 'deadline'
  | 'amendment'
  | 'transition'
  | 'reporting'
  | 'consultation_close'

/** `'all'` drops the upper bound — see KAN-256 and the API schema note. */
export type RegulatoryDeadlineWindow = '30' | '90' | '365' | 'all'

export type RegulatoryCalendarFilters = {
  /** Framework slug, framework UUID, or `'all'` for no filter. */
  framework: string
  /** Deadline kind, or `'all'` for no filter. */
  kind: RegulatoryDeadlineKind | 'all'
  window: RegulatoryDeadlineWindow
}

export type RegulatoryDeadlineRow = {
  id: string
  frameworkId: string
  frameworkSlug: string
  frameworkName: string
  kind: RegulatoryDeadlineKind
  /** YYYY-MM-DD */
  dueDate: string
  label: string
  description: string | null
  sourceUrl: string
}

export type RegulatoryDeadlineGroup = {
  /** e.g. `2026Q2`. */
  quarter: string
  deadlines: RegulatoryDeadlineRow[]
}

export type RegulatoryDeadlinesResponse = {
  window: RegulatoryDeadlineWindow
  filters: {
    framework: string | null
    kind: string | null
  }
  groups: RegulatoryDeadlineGroup[]
}

type RegulatoryDeadlinesPage = RegulatoryDeadlinesResponse & {
  pageInfo: {
    hasMore: boolean
    nextCursor: string | null
  }
}

async function loadAllRegulatoryDeadlines(
  baseParams: URLSearchParams,
): Promise<RegulatoryDeadlinesResponse> {
  const groups: RegulatoryDeadlineGroup[] = []
  const seenCursors = new Set<string>()
  let cursor: string | null = null
  let firstPage: RegulatoryDeadlinesPage | null = null

  for (;;) {
    const params = new URLSearchParams(baseParams)
    if (cursor) params.set('cursor', cursor)
    const page = await api.get<RegulatoryDeadlinesPage>(
      `/regulatory/deadlines?${params.toString()}`,
    )
    firstPage ??= page
    for (const group of page.groups) {
      const previous = groups.at(-1)
      if (previous?.quarter === group.quarter) {
        previous.deadlines.push(...group.deadlines)
      } else {
        groups.push({ ...group, deadlines: [...group.deadlines] })
      }
    }

    const nextCursor = page.pageInfo?.nextCursor ?? null
    if (page.pageInfo?.hasMore && !nextCursor) {
      throw new Error('regulatory_deadline_cursor_missing')
    }
    if (!page.pageInfo?.hasMore) break
    if (seenCursors.has(nextCursor!)) {
      throw new Error('regulatory_deadline_cursor_repeated')
    }
    seenCursors.add(nextCursor!)
    cursor = nextCursor
  }

  if (!firstPage) throw new Error('regulatory_deadline_page_missing')
  return {
    window: firstPage.window,
    filters: firstPage.filters,
    groups,
  }
}

export function useRegulatoryDeadlines(filters: RegulatoryCalendarFilters) {
  const params = new URLSearchParams({ window: filters.window })
  if (filters.framework && filters.framework !== 'all') {
    params.set('framework', filters.framework)
  }
  if (filters.kind && filters.kind !== 'all') {
    params.set('kind', filters.kind)
  }
  return useQuery({
    queryKey: [
      'regulatory-deadlines',
      filters.window,
      filters.framework,
      filters.kind,
    ],
    retry: publicReadRetry,
    queryFn: () => loadAllRegulatoryDeadlines(params),
  })
}

// ─── Compare ────────────────────────────────────────────────────────────────

export type RegulatoryCompareFrameworkSummary = {
  id: string
  slug: string
  name: string
  jurisdiction: string
  coverageStatus: string
  briefId: string
  updatedAt: string | Date
}

export type RegulatoryCompareField =
  | 'scopeSummary'
  | 'applicability'
  | 'keyObligations'
  | 'penaltyStructure'
  | 'recentAmendments'
  | 'crosswalk'
  | 'citationLog'
  | 'analystPublicNotes'

export type RegulatoryCompareFieldRow = {
  field: RegulatoryCompareField
  a: unknown
  b: unknown
}

export type RegulatoryCompareResponse = {
  frameworks: {
    a: RegulatoryCompareFrameworkSummary
    b: RegulatoryCompareFrameworkSummary
  }
  fields: RegulatoryCompareFieldRow[]
}

export function useRegulatoryCompare(
  frameworkAId: string | undefined,
  frameworkBId: string | undefined,
) {
  const enabled = Boolean(
    frameworkAId && frameworkBId && frameworkAId !== frameworkBId,
  )
  return useQuery({
    queryKey: [
      'regulatory-compare',
      frameworkAId ?? 'missing-a',
      frameworkBId ?? 'missing-b',
    ],
    enabled,
    // A 422 here means "these two frameworks cannot form a valid pair".
    // That is the answer, not a blip — retrying it three times with backoff
    // just bought ~8.3s of skeleton before showing the same message.
    retry: publicReadRetry,
    queryFn: () =>
      api.get<RegulatoryCompareResponse>(
        `/regulatory/compare?frameworkAId=${encodeURIComponent(
          frameworkAId!,
        )}&frameworkBId=${encodeURIComponent(frameworkBId!)}`,
      ),
  })
}

// ─── Watches ────────────────────────────────────────────────────────────────

export type RegulatoryWatchFramework = {
  id: string
  slug: string
  name: string
  shortDescription: string
  jurisdiction: string
  referenceUrl: string
  coverageStatus: string
}

export type RegulatoryWatchEntry = {
  createdAt: string | Date
  framework: RegulatoryWatchFramework
}

export type RegulatoryWatchesResponse = {
  watches: RegulatoryWatchEntry[]
}

export function useRegulatoryWatches() {
  return useQuery({
    queryKey: ['regulatory-watches'],
    // NOT `publicReadRetry`. Unlike every other read in this file,
    // `/regulatory/watches` is session-authenticated and user-scoped
    // (`WHERE ufw.user_id = $userId` in regulatory-watches.routes.ts), so it
    // falls under the stricter policy written for exactly that shape after
    // the 2026-04-30 retry-storm smoke: no retry on 4xx, one retry on 5xx,
    // none on network errors.
    retry: authGatedReadRetry,
    queryFn: () => api.get<RegulatoryWatchesResponse>('/regulatory/watches'),
  })
}

export function useAddRegulatoryWatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (frameworkId: string) =>
      api.post<{ watched: true; frameworkId: string }>(
        `/regulatory/watches/${encodeURIComponent(frameworkId)}`,
        {},
      ),
    onSuccess: (_data, frameworkId) => {
      // KAN-32 regulatory:watch_added — from the mutation variables, before
      // the invalidations.
      trackRegulatoryWatchAdded(frameworkId)
      queryClient.invalidateQueries({ queryKey: ['regulatory-watches'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'regulatory-tile'] })
    },
  })
}

// ─── Dashboard regulatory tile ───────────────────────────────────────────────

/** Shared framework shape used in both personalised and default tile modes. */
export type RegulatoryTileFramework = {
  id: string
  slug: string
  name: string
  shortDescription: string
  jurisdiction: string
  referenceUrl: string
  coverageStatus: string
}

/** Shared deadline shape used in personalised and default tile modes. */
export type RegulatoryTileDeadline = {
  id: string
  frameworkId: string
  frameworkSlug: string
  frameworkName: string
  kind: string
  /** ISO date string, e.g. "2026-06-01" */
  dueDate: string
  label: string
  sourceUrl: string
}

/**
 * Discriminated union matching the `GET /api/dashboard/regulatory-tile`
 * response contract (apps/api/src/routes/dashboard.routes.ts:460-635).
 */
export type RegulatoryTileResponse =
  | {
      mode: 'personalised'
      watchedFrameworks: RegulatoryTileFramework[]
      deadlines: RegulatoryTileDeadline[]
    }
  | {
      mode: 'default'
      frameworks: RegulatoryTileFramework[]
      deadlines: RegulatoryTileDeadline[]
      footer: string
    }
  | {
      mode: 'cta'
      deadlines: []
      cta: { label: string; href: string }
    }

export function useDeleteRegulatoryWatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (frameworkId: string) =>
      api.delete<{ deleted: true; frameworkId: string }>(
        `/regulatory/watches/${encodeURIComponent(frameworkId)}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regulatory-watches'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'regulatory-tile'] })
    },
  })
}
